/**
 * ADR-016 — the cancellation chooser.
 *
 * One shared, context-aware dialog that explains the cancel fork at the moment
 * of the decision, so a treasurer never faces two bare buttons they must
 * pre-understand. Every destructive option leads with the unmissable promise
 * "this will not be on your report"; the only consequence that differs is
 * permanence (Void is recoverable, Delete is not). Restore is the one option
 * that brings a row BACK, so it leads with its own "for review" framing.
 *
 * The offered pair is selected from `(rowKind, status, resolvedAs)` — NEVER
 * `ledgerStatus`, because the slim Expenditure/Refund triage DTOs deliberately
 * omit it. Variants:
 *
 *   | (rowKind, status, resolvedAs)                          | Offered           |
 *   |--------------------------------------------------------|-------------------|
 *   | contribution/expenditure, not Reported, not Voided     | Void / Delete     |
 *   | refund, not Reported                                   | Delete only       |
 *   | contribution/expenditure, resolvedAs = Voided          | Restore / Delete  |
 *   | any money kind, status = Reported                      | amend message     |
 *
 * Refunds are Delete-only in v1 (no Void / un-void — ADR-016 §5), so the
 * refund arm is selected BEFORE the Voided arm: a refund never offers Restore,
 * even in the (v1-impossible) event one is somehow Voided.
 *
 * The import-batch (Undo Import) arm is DEFERRED — no Undo Import host UI
 * exists; the backend undo is still a destructive delete.
 *
 * Mirrors `ResolutionOptionsDialog` (controlled shadcn `Dialog` + full-width
 * icon/title/subtitle choice buttons); rose-destructive styling for the heavy
 * Delete arm. All copy lives in `PreflightCopy` so it renders at the boundary
 * and no engineer enum identifier ever leaks into treasurer-facing text.
 */
import { useEffect, useState } from 'react';
import { Ban, RotateCcw, Trash2 } from 'lucide-react';
import { PreflightCopy, assertNever, ErrorCode, isStaleWrite } from '@signalvote/shared';
import type { RowVersion, TriageItemStatus, TriageResolvedAs } from '@signalvote/shared';
import { cn } from '@/lib/utils';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  useVoidContribution,
  useUnvoidContribution,
  useDeleteContribution,
  useVoidExpenditure,
  useUnvoidExpenditure,
  useDeleteExpenditure,
  useDeleteContributionRefund,
  StagedRowUpdateError,
} from '@/hooks/queries';
import {
  FiledReportAmendLink,
  filedReportRefusal,
  plainRefusal,
  type DeleteRefusal,
} from '@/components/finance/shared';

// ============================================================================
// Types
// ============================================================================

// UI-layer vocabulary for the chooser payload — deliberately the lowercase
// money-row subset of the PascalCase wire `TriageItem['kind']` (BankLine is
// excluded; bank lines never reach the chooser). The wire→UI mapping is explicit
// in each call site (e.g. `moneyRowChooserPayload` in the ledger), so this is a
// new local string set, not a redefinition of the shared kind.
export type CancellationChooserRowKind =
  | 'contribution'
  | 'expenditure'
  | 'refund';

/**
 * The minimal row identity the chooser needs. Built from a `TriageItem` (the
 * live ledger / triage modal) or a detail DTO (the staged panel). Variant
 * selection keys on `(rowKind, status, resolvedAs)` — never `ledgerStatus`.
 */
export interface CancellationChooserPayload {
  rowKind: CancellationChooserRowKind;
  rowId: string;
  status: TriageItemStatus;
  resolvedAs: TriageResolvedAs | null;
  /** Optimistic-concurrency token for the void / un-void / delete request. */
  concurrencyVersion: RowVersion;
}

interface CancellationChooserDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** The row to act on; null while the dialog is closed. */
  payload: CancellationChooserPayload | null;
  /** Fired after a successful Void / Restore / Delete (host advances / refetches). */
  onResolved?: () => void;
}

type ChooserVariant = 'live' | 'refund' | 'voided' | 'reported';

function selectVariant(payload: CancellationChooserPayload): ChooserVariant {
  if (payload.status === 'Reported') return 'reported';
  // Refund is Delete-only (ADR-016 §5) — never Void / Restore. Selected before
  // the Voided arm so a refund can never offer Restore.
  if (payload.rowKind === 'refund') return 'refund';
  if (payload.resolvedAs === 'Voided') return 'voided';
  return 'live';
}

/**
 * Translates a mutation failure to treasurer copy at the render boundary — the
 * specific delete-refusal codes come first, then the calendar-lock refusal relays the
 * server's reason, then a generic 409 falls back to the refresh prompt. No raw code or
 * backend identifier ever reaches the dialog.
 */
function chooserRefusal(err: unknown): DeleteRefusal {
  // ADR-081 first — the entry is on a report already sent to the regulator. Named, and
  // paired with a link so the amendment path is one click from the refusal.
  const onFiledReport = filedReportRefusal(err);
  if (onFiledReport) return onFiledReport;

  if (err instanceof StagedRowUpdateError) {
    if (err.code === ErrorCode.DeleteHasDependentRefunds) {
      return plainRefusal(PreflightCopy.ChooserDependentRefundsError);
    }
    if (err.code === ErrorCode.DeleteReferencedByFiling) {
      return plainRefusal(PreflightCopy.ChooserReferencedByFilingError);
    }
    // The calendar lock with no in-app report to name (a filed one refuses with
    // SupportsFiledReport above), such as history reported before onboarding. There is no
    // report to link, so relay the server's reason.
    if (err.code === ErrorCode.StagedRowUpdateAlreadyReported) {
      return plainRefusal(err.message);
    }
    if (err.status === 409 || isStaleWrite(err)) {
      return plainRefusal(PreflightCopy.ChooserConcurrencyError);
    }
    return plainRefusal(err.message);
  }
  return plainRefusal(err instanceof Error ? err.message : 'Something went wrong. Try again.');
}

// ============================================================================
// Component
// ============================================================================

export function CancellationChooserDialog({
  open,
  onOpenChange,
  payload,
  onResolved,
}: CancellationChooserDialogProps) {
  const [error, setError] = useState<DeleteRefusal | null>(null);

  const { mutateAsync: voidContribution, isPending: voidingContribution } =
    useVoidContribution();
  const { mutateAsync: unvoidContribution, isPending: unvoidingContribution } =
    useUnvoidContribution();
  const { mutateAsync: deleteContribution, isPending: deletingContribution } =
    useDeleteContribution();
  const { mutateAsync: voidExpenditure, isPending: voidingExpenditure } =
    useVoidExpenditure();
  const { mutateAsync: unvoidExpenditure, isPending: unvoidingExpenditure } =
    useUnvoidExpenditure();
  const { mutateAsync: deleteExpenditure, isPending: deletingExpenditure } =
    useDeleteExpenditure();
  const { mutateAsync: deleteRefund, isPending: deletingRefund } =
    useDeleteContributionRefund();

  const isPending =
    voidingContribution ||
    unvoidingContribution ||
    deletingContribution ||
    voidingExpenditure ||
    unvoidingExpenditure ||
    deletingExpenditure ||
    deletingRefund;

  // Clear the inline error whenever the dialog re-opens onto a (possibly
  // different) row so a stale message can't bleed across rows.
  useEffect(() => {
    if (open) setError(null);
  }, [open, payload?.rowId]);

  const handleOpenChange = (next: boolean) => {
    if (!next) setError(null);
    onOpenChange(next);
  };

  const run = async (mutate: () => Promise<unknown>) => {
    setError(null);
    try {
      await mutate();
      // The void / unvoid / delete hooks invalidate the sort-inbox + list keys,
      // so the host surface repaints; `onResolved` only advances / closes it.
      onResolved?.();
      handleOpenChange(false);
    } catch (err) {
      setError(chooserRefusal(err));
    }
  };

  const doVoid = () => {
    if (!payload) return;
    const { rowId, concurrencyVersion } = payload;
    if (payload.rowKind === 'contribution') {
      return run(() => voidContribution({ row: { id: rowId, concurrencyVersion } }));
    }
    return run(() => voidExpenditure({ row: { id: rowId, concurrencyVersion } }));
  };

  const doRestore = () => {
    if (!payload) return;
    const { rowId, concurrencyVersion } = payload;
    if (payload.rowKind === 'contribution') {
      return run(() => unvoidContribution({ row: { id: rowId, concurrencyVersion } }));
    }
    return run(() => unvoidExpenditure({ row: { id: rowId, concurrencyVersion } }));
  };

  const doDelete = () => {
    if (!payload) return;
    const { rowId, concurrencyVersion } = payload;
    switch (payload.rowKind) {
      case 'contribution':
        return run(() => deleteContribution({ row: { id: rowId, concurrencyVersion } }));
      case 'expenditure':
        return run(() => deleteExpenditure({ row: { id: rowId, concurrencyVersion } }));
      case 'refund':
        return run(() => deleteRefund({ row: { id: rowId, concurrencyVersion } }));
      default:
        return assertNever(payload.rowKind);
    }
  };

  const variant = payload ? selectVariant(payload) : null;
  const isReported = variant === 'reported';

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent className="max-w-md" data-testid="cancellation-chooser-dialog">
        <DialogHeader>
          <DialogTitle>
            {isReported
              ? PreflightCopy.ChooserReportedTitle
              : PreflightCopy.ChooserDialogTitle}
          </DialogTitle>
          <DialogDescription
            // The Reported variant's description IS the amend message (a message,
            // not a route — ADR-007); the action variants get a neutral subtitle.
            data-testid={isReported ? 'chooser-reported-amend' : undefined}
          >
            {isReported
              ? PreflightCopy.ChooserReportedAmendMessage
              : PreflightCopy.ChooserDialogSubtitle}
          </DialogDescription>
        </DialogHeader>

        {variant !== 'reported' && payload && (
          <div className="space-y-2">
            {variant === 'live' && (
              <ChoiceButton
                testId="chooser-void"
                icon={<Ban className="h-5 w-5 shrink-0 text-muted-foreground" />}
                title={PreflightCopy.ChooserVoidOptionTitle}
                lead={PreflightCopy.ChooserNotOnReportPromise}
                body={PreflightCopy.ChooserVoidOptionBody}
                disabled={isPending}
                onClick={doVoid}
              />
            )}

            {variant === 'voided' && (
              <ChoiceButton
                testId="chooser-restore"
                icon={<RotateCcw className="h-5 w-5 shrink-0 text-muted-foreground" />}
                title={PreflightCopy.ChooserRestoreOptionTitle}
                lead={PreflightCopy.ChooserRestoreFraming}
                disabled={isPending}
                onClick={doRestore}
              />
            )}

            <ChoiceButton
              testId="chooser-delete"
              destructive
              icon={<Trash2 className="h-5 w-5 shrink-0 text-rose-600" />}
              title={PreflightCopy.ChooserDeleteOptionTitle}
              lead={PreflightCopy.ChooserNotOnReportPromise}
              body={PreflightCopy.ChooserDeleteOptionBody}
              warning={PreflightCopy.ChooserDeletePermanentLine}
              disabled={isPending}
              onClick={doDelete}
            />
          </div>
        )}

        {error && (
          <div
            role="alert"
            data-testid="chooser-error"
            className="rounded-md border border-rose-200 bg-rose-50 px-3 py-2 text-sm text-rose-700"
          >
            {error.message}
            {error.filedReport && (
              <FiledReportAmendLink
                filedReport={error.filedReport}
                testId="chooser-filed-report-link"
              />
            )}
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}

// ============================================================================
// Choice button — full-width icon/title/subtitle row (mirrors
// ResolutionOptionsDialog). `items-start` + wrapping copy keeps it legible at
// font-scale 1.3 (no clipping). The destructive arm gets rose styling.
// ============================================================================

function ChoiceButton({
  testId,
  icon,
  title,
  lead,
  body,
  warning,
  destructive = false,
  disabled,
  onClick,
}: {
  testId: string;
  icon: React.ReactNode;
  title: string;
  lead: string;
  body?: string;
  warning?: string;
  destructive?: boolean;
  disabled: boolean;
  onClick: () => void;
}) {
  return (
    <button
      type="button"
      data-testid={testId}
      disabled={disabled}
      onClick={onClick}
      className={cn(
        'flex w-full items-start gap-3 rounded-lg border p-3 text-left transition-colors disabled:opacity-50 disabled:cursor-not-allowed',
        destructive
          ? 'border-rose-300 hover:bg-rose-50'
          : 'hover:bg-accent',
      )}
    >
      {icon}
      <div className="min-w-0">
        <p
          className={cn(
            'text-sm font-medium',
            destructive && 'text-rose-700',
          )}
        >
          {title}
        </p>
        <p className="text-xs text-muted-foreground">{lead}</p>
        {body && <p className="mt-0.5 text-xs text-muted-foreground">{body}</p>}
        {warning && (
          <p className="mt-0.5 text-xs font-medium text-rose-700">{warning}</p>
        )}
      </div>
    </button>
  );
}
